import { z } from 'zod';
import type { HttpService } from '@web/data/services';

/**
 * Lean CRM search hit. Mirrors the customer-api `SearchHitSchema` (file-local there);
 * the P1 kanban bridge passes it through untyped, so the web owns the parse. `type` is
 * the web-side discriminator that unions with the domain `CardSearchResult` (`type:'card'`)
 * and is attached on parse via `.transform` (the bridge does not inject it).
 */
export const CustomerHitFieldsSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  companyId: z.string().nullable(),
  companyName: z.string().nullable(),
  vip: z.boolean(),
});

const CustomerHitSchema = CustomerHitFieldsSchema.transform((hit) => ({
  type: 'customer' as const,
  ...hit,
}));

export type CustomerHit = z.infer<typeof CustomerHitSchema>;

export interface CustomerSearchRepository {
  search: (orgId: string, q: string, limit?: number) => Promise<CustomerHit[]>;
}

export function createCustomerSearchRepository(http: HttpService): CustomerSearchRepository {
  return {
    search: async (orgId, q, limit = 20) => {
      // The P1 bridge returns a BARE array (no { results } wrapper) — parse and return directly.
      return http.get(`/orgs/${encodeURIComponent(orgId)}/customers/search`, {
        query: { q, limit },
        schema: z.array(CustomerHitSchema),
      });
    },
  };
}
